import React, { useState, useEffect, useRef, useCallback } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { getRequest, pathname } from "@react/helpers/fetch"

import Card             from "@react/shared/ui/Card"
import Header           from "./components/Header"
import StatsStrip       from "./components/StatsStrip"
import FilterBar        from "./components/FilterBar"
import MbcContractsTable from "./components/MbcContractsTable"
import Pagination       from "./components/Pagination"
import CsvExportModal   from "./components/CsvExportModal"

const DEBOUNCE_MS = 300

const App = ({ data: initialData }) => {
  const [data,         setData]         = useState(initialData)
  const [page,         setPage]         = useState(initialData.pagination?.current_page || 1)
  const [order,        setOrder]        = useState(initialData.order)
  const [direction,    setDirection]    = useState(initialData.direction)
  const [statusFilter, setStatusFilter] = useState(initialData.status_filter || "")
  const [search,       setSearch]       = useState(initialData.search || "")
  const [loading,      setLoading]      = useState(false)
  const [exportOpen,   setExportOpen]   = useState(false)

  const indexUrl     = initialData.urls.index
  const didMountRef  = useRef(false)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  const fetchIndex = useCallback(() => {
    const requestId = ++requestIdRef.current
    const params    = { page, order, direction, status: statusFilter, search }

    setLoading(true)
    return getRequest(pathname({ path: indexUrl, format: "json", params })).then(json => {
      if (requestId !== requestIdRef.current) return
      setData(json)
      setLoading(false)
      window.history.replaceState(null, "", pathname({ path: indexUrl, params }))
    })
  }, [indexUrl, page, order, direction, statusFilter, search])

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchIndex, DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [fetchIndex])

  const handleSortChange = (nextOrder, nextDirection) => {
    setOrder(nextOrder); setDirection(nextDirection); setPage(1)
  }

  const handleStatusChange = nextStatus => {
    setStatusFilter(nextStatus); setPage(1)
  }

  const handleSearchChange = nextSearch => {
    setSearch(nextSearch); setPage(1)
  }

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="px-8 py-8">
        <Header
          count         = {data.stats?.count ?? 0}
          urls          = {data.urls}
          canCreate     = {data.can_create}
          canExport     = {data.can_export}
          onExportClick = {() => setExportOpen(true)}
        />

        <StatsStrip stats={data.stats} />

        <Card className={`rounded-lg relative ${loading ? "opacity-75" : ""}`}>
          <FilterBar
            search         = {search}
            onSearchChange = {handleSearchChange}
            statusFilter   = {statusFilter}
            onStatusChange = {handleStatusChange}
            statuses       = {data.statuses}
          />

          <MbcContractsTable
            mbcContracts = {data.mbc_contracts}
            order        = {order}
            direction    = {direction}
            onSortChange = {handleSortChange}
          />

          <Pagination
            pagination   = {data.pagination}
            onPageChange = {setPage}
          />
        </Card>
      </div>

      {data.can_export && data.urls.export && (
        <CsvExportModal
          isOpen       = {exportOpen}
          onClose      = {() => setExportOpen(false)}
          csvUrl       = {data.urls.export}
          ids          = {data.export_ids}
          count        = {data.export_ids?.length ?? 0}
          defaultEmail = {data.current_user_email}
        />
      )}
    </div>
  )
}

export default App
